/**
 * VendorSelectPopover — the vendor an item will be bought from, rendered as a
 * link, with a picker behind it.
 *
 * A canonical item has several ways to buy it, and the person counting often
 * knows which one applies this week. Everywhere the count tool or the template
 * editor names a vendor, that name is this control: it shows the vendor
 * currently resolved for the line (an explicit choice, else the automatic
 * lowest trustworthy comparable price, else the preferred vendor as fallback)
 * and opens a list of the alternatives — each priced two ways: the real
 * package price under its real purchase-unit word, and the comparable price
 * in the relationship's comparison unit.
 *
 * It shows a plain, non-interactive label when there is nothing to choose
 * between — one vendor product, or none. A link that opens a list of one is
 * a lie about the options available.
 *
 * Desktop opens an anchored popover; `mobile` opens a bottom sheet portaled to
 * document.body. Escape closes either, focus returns to the trigger, and every
 * control stops propagation so a tap never reaches the count row behind it.
 *
 * Props:
 *   options          - purchase_options rows for the item (any status)
 *   countUnit        - the item's count unit, for per-unit prices
 *   selectedOptionId - count_items.selected_purchase_option_id (null = default)
 *   vendorNameById?  - { [vendorId]: display_name }
 *   onSelect         - (optionId | null) => void | Promise; null clears the pin
 *   mobile?          - bottom sheet instead of a popover
 *   itemName?        - shown as the picker's heading
 *   align?           - 'left' | 'right' popover edge (default 'left')
 *   disabled?        - render the label without the link affordance
 *   compact?         - smaller type, for dense rows
 *   openOnHover?     - desktop: reveal the list on hover-intent as well as on
 *                      click. Off by default — on a dense count tape a pointer
 *                      travelling across rows would pop panels open behind the
 *                      number being typed.
 *   item?            - inventory item row (comparison_* columns feed the
 *                      stored relationship comparison; optional)
 *   countFormatPinned? - this line's count format is pinned, so the footer
 *                      must not claim the displayed package follows the vendor
 *   note?            - overrides the footer sentence describing what choosing
 *                      does; when omitted the default states the comparison
 *                      unit and whether the displayed package follows.
 *
 * When no single product is resolved for the line but the item has several
 * vendors, the label reads "Multi-vendor" rather than "No vendor". The item
 * has vendors; what it lacks is a CHOICE, and saying "No vendor" sends people
 * to fix a link that is not broken.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  activeOptions,
  resolveSelectedOption,
  vendorLabelOf,
  formatMoney,
} from '../utils/itemCostSummary';
import { buildComparablePriceDisplay, purchaseUnitLabel } from '../utils/priceComparison';
import { vendorIsRetired } from '../utils/purchaseOptions';

// Matches the hover-intent delay the count card's MultiVendorBadge uses, so
// the two multi-vendor affordances feel like one control.
const HOVER_INTENT_MS = 150;
const HOVER_LEAVE_MS = 180;

const C = {
  ink: '#3E2723',
  body: '#5A4A42',
  muted: '#8B7B73',
  line: '#E8DDD0',
  hairline: '#F1EAE2',
  surface: '#FEFCFA',
  link: '#8A5A00',
  amberBg: '#FFF4E0',
  amberLine: '#E6B866',
  green: '#2E7D32',
  greenBg: '#E8F5E9',
};

const styles = {
  wrap: { position: 'relative', display: 'inline-flex', minWidth: 0, maxWidth: '100%' },
  link: (compact) => ({
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontSize: compact ? '0.72rem' : '0.8rem',
    fontWeight: 600,
    color: C.link,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    cursor: 'pointer',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    minHeight: compact ? 20 : 24,
  }),
  plain: (compact) => ({
    fontSize: compact ? '0.72rem' : '0.8rem',
    color: C.body,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  }),
  caret: { fontSize: '0.6em', marginLeft: 3, opacity: 0.75 },
  popover: (align) => ({
    position: 'absolute',
    top: 'calc(100% + 6px)',
    [align === 'right' ? 'right' : 'left']: 0,
    zIndex: 10040,
    width: 288,
    maxWidth: '84vw',
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(62,39,35,0.22)',
    padding: '10px 10px 8px',
    textAlign: 'left',
    cursor: 'default',
    // Hosts vary — a table cell or a count row may set nowrap/ellipsis on its
    // subtree. The panel resets typography so it renders the same everywhere.
    whiteSpace: 'normal',
    lineHeight: 1.35,
  }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(62,39,35,0.45)', zIndex: 10050 },
  sheet: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 10051,
    background: C.surface, borderRadius: '16px 16px 0 0',
    border: `1px solid ${C.line}`, borderBottom: 'none',
    boxShadow: '0 -8px 30px rgba(0,0,0,0.25)',
    padding: '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
    maxHeight: '76vh', overflowY: 'auto',
    whiteSpace: 'normal', lineHeight: 1.35,
  },
  sheetHandle: { width: 36, height: 4, borderRadius: 2, background: '#D9CEC2', margin: '0 auto 10px' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 8, marginBottom: 8, minWidth: 0,
  },
  headerText: { minWidth: 0 },
  title: {
    fontWeight: 700, fontSize: 13, color: C.ink, lineHeight: 1.3,
    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical', wordBreak: 'break-word',
  },
  subtitle: { fontSize: 11, color: C.muted, marginTop: 2 },
  close: {
    background: 'none', border: 'none', fontSize: 18, lineHeight: 1, cursor: 'pointer',
    color: C.muted, padding: '0 2px', flexShrink: 0, minWidth: 28, minHeight: 28,
  },
  choice: (selected) => ({
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: `1.5px solid ${selected ? C.amberLine : C.hairline}`,
    background: selected ? C.amberBg : '#fff',
    borderRadius: 9,
    padding: '7px 9px',
    marginBottom: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minHeight: 44,
  }),
  choiceTop: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  vendor: { fontWeight: 700, fontSize: 12.5, color: C.ink, wordBreak: 'break-word' },
  tag: (bg, fg) => ({
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
    background: bg, color: fg, borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap',
  }),
  meta: { fontSize: 11, color: C.muted, marginTop: 3, wordBreak: 'break-word' },
  price: { fontSize: 11.5, color: C.ink, fontWeight: 700, marginTop: 2 },
  note: { fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.35 },
  stale: {
    fontSize: 11, color: '#8A5A00', background: C.amberBg, border: `1px solid ${C.amberLine}`,
    borderRadius: 8, padding: '6px 8px', marginBottom: 8, lineHeight: 1.35,
  },
};

export default function VendorSelectPopover({
  options,
  countUnit,
  selectedOptionId,
  vendorNameById = {},
  onSelect,
  mobile = false,
  itemName,
  item = null,
  align = 'left',
  disabled = false,
  compact = false,
  openOnHover = false,
  countFormatPinned = false,
  note = null,
}) {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState(undefined);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const hoverTimer = useRef(null);

  const active = activeOptions(options);
  const resolution = resolveSelectedOption(options, selectedOptionId, { item });
  const current = resolution.option;
  const display = buildComparablePriceDisplay({ item: item || {}, options, selectedOptionId, vendorNameById });
  const displayRowById = new Map(display.options.map((row) => [String(row.optionId), row]));
  const unitLabel = display.comparisonAvailable ? display.comparisonUnitLabel : null;

  // What choosing here does. Default copy states the comparison unit when one
  // is resolved; the pinned-format variant must not claim the displayed count
  // package will move, because a pinned format stays pinned.
  const effectiveNote = note ?? (unitLabel
    ? (countFormatPinned
      ? `Countertop compares each vendor package in ${unitLabel}. Changing vendors changes the purchase source. This line's pinned count format stays as pinned — the saved quantity is never recounted or lost.`
      : `Countertop compares each vendor package in ${unitLabel}. Changing vendors changes the purchase source and, when this count follows purchasing, the displayed package. The saved quantity is converted—not recounted or lost.`)
    : 'Changing the vendor here affects what this line is ordered from. It does not change the count, the count unit, or the item itself.');

  // The vendors actually behind this item, deduplicated by name — two packs
  // from Sysco are one vendor, not two.
  const vendorLabels = [...new Set(
    active.map((o) => vendorLabelOf(o, vendorNameById)).filter(Boolean),
  )];

  // "No vendor" is only true when there are none. An item bought four ways,
  // with no one product pinned to this line, has four vendors and no CHOICE
  // yet — reading that back as "No vendor" says the opposite of what is true
  // and sends people off to fix a link that is not broken.
  const currentLabel = current
    ? (vendorLabelOf(current, vendorNameById) || 'Vendor not linked')
    : vendorLabels.length >= 2
      ? 'Multi-vendor'
      : vendorLabels[0] || 'No vendor';

  // Nothing to choose between → a plain label. Also the "no options at all"
  // case, where a picker would open on an empty list.
  const selectable = !disabled && typeof onSelect === 'function' && active.length >= 2;

  const close = () => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  /**
   * Hover-intent, desktop only.
   *
   * The delay is what makes this usable rather than twitchy: a pointer crossing
   * the row on its way somewhere else must not pop a panel open. Closing is
   * also delayed, so the pointer can travel off the trigger and into the panel
   * without the panel vanishing under it. A panel opened by hover still closes
   * on click, Escape, or an outside press, exactly like a clicked one.
   */
  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  const hoverOpen = () => {
    if (!openOnHover || mobile) return;
    cancelHover();
    hoverTimer.current = setTimeout(() => setOpen(true), HOVER_INTENT_MS);
  };

  const hoverClose = () => {
    if (!openOnHover || mobile) return;
    cancelHover();
    hoverTimer.current = setTimeout(() => setOpen(false), HOVER_LEAVE_MS);
  };

  useEffect(() => cancelHover, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') { event.stopPropagation(); close(); } };
    const onDocDown = (event) => {
      if (mobile) return;
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDocDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDocDown);
    };
  }, [open, mobile]);

  if (!selectable) {
    return (
      <span style={styles.plain(compact)} title={vendorLabels.join(' · ') || currentLabel} data-testid="vendor-select-plain">
        {currentLabel}
      </span>
    );
  }

  const choose = async (optionId) => {
    setPendingId(optionId);
    try {
      await onSelect(optionId);
      setOpen(false);
    } catch (err) {
      console.warn('[vendor-select] Could not change vendor:', err?.message);
    } finally {
      setPendingId(undefined);
    }
  };

  // How this line came to point at its option, phrased for the tag row. Only
  // the resolved option wears it; 'only' needs no tag (there was no choice).
  const selectionTag = (() => {
    switch (resolution.reason) {
      case 'explicit': return { text: 'Selected', bg: C.greenBg, fg: C.green };
      case 'best_price':
      case 'best_price_ai_estimate':
      case 'tied_best_price':
        return { text: 'Auto — lowest price', bg: C.greenBg, fg: C.green };
      case 'preferred_fallback': return { text: 'Preferred fallback', bg: C.amberBg, fg: C.link };
      default: return null;
    }
  })();

  const renderChoice = (option) => {
    const isSelected = current && String(current.id) === String(option.id);
    const row = displayRowById.get(String(option.id));
    const label = vendorLabelOf(option, vendorNameById) || 'Vendor not linked';
    // A retired vendor's product still describes stock on the shelf, so it
    // stays listed — but it cannot be chosen as the purchase source, and it
    // says why instead of silently vanishing from a list somebody remembers
    // being longer.
    const retired = vendorIsRetired(option);
    const product = option.vendor_description && option.vendor_description !== label
      ? option.vendor_description
      : null;
    // The package price wears its REAL purchase-unit word ('per case',
    // 'per bag'); the comparable price wears the relationship unit. Inner
    // units (pouches, bags) never label a price they don't describe.
    const packagePriceText = formatMoney(option.package_price)
      ? `${formatMoney(option.package_price)} ${row?.purchaseUnitLabel ?? purchaseUnitLabel(option)}`
      : 'No package price';
    const comparableText = row?.comparableDisplay ?? null;
    return (
      <button
        key={option.id}
        type="button"
        style={styles.choice(isSelected)}
        aria-pressed={!!isSelected}
        disabled={pendingId !== undefined || retired}
        data-testid="vendor-choice"
        onClick={(event) => {
          event.stopPropagation();
          if (!retired) choose(option.id);
        }}
      >
        <span style={styles.choiceTop}>
          <span style={styles.vendor}>{label}</span>
          {isSelected && selectionTag && (
            <span style={styles.tag(selectionTag.bg, selectionTag.fg)}>{selectionTag.text}</span>
          )}
          {row?.isLowest && <span style={styles.tag(C.greenBg, C.green)}>Lowest comparable price</span>}
          {row?.isTiedLowest && <span style={styles.tag(C.greenBg, C.green)}>Tied lowest</span>}
          {row?.estimated && <span style={styles.tag(C.amberBg, C.link)}>Estimated</span>}
        </span>
        {product && <span style={{ ...styles.meta, display: 'block' }}>{product}</span>}
        <span style={{ ...styles.meta, display: 'block' }}>
          {[option.pack_description || option.purchase_unit || null,
            option.vendor_sku ? `SKU ${option.vendor_sku}` : null,
            // A retired vendor's minimum constrains nobody.
            !retired && option.moq_packages ? `MOQ ${option.moq_packages}` : null,
          ].filter(Boolean).join(' · ') || 'No pack details'}
        </span>
        {retired && (
          <span style={{ ...styles.meta, display: 'block', color: C.link }} data-testid="vendor-choice-retired">
            No longer ordering from this vendor — package kept for counting existing stock
          </span>
        )}
        <span style={{ ...styles.price, display: 'block' }}>
          {packagePriceText}
          {comparableText ? ` · ${comparableText}` : ' · comparison unavailable'}
        </span>
        {row?.isStale && comparableText && (
          <span style={{ ...styles.meta, display: 'block', color: C.link }}>Price may be outdated</span>
        )}
      </button>
    );
  };

  const panel = (
    <div data-testid="vendor-select-panel" onClick={(e) => e.stopPropagation()}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>Buy {itemName ? `“${itemName}”` : 'this item'} from</div>
          <div style={styles.subtitle}>
            {active.length} vendor option{active.length === 1 ? '' : 's'}
            {unitLabel ? ` · compared by ${unitLabel}` : ''}
          </div>
        </div>
        <button type="button" aria-label="Close vendor picker" style={styles.close} onClick={close}>&times;</button>
      </div>

      {resolution.isStale && (
        <div style={styles.stale} role="status">
          The vendor product chosen for this line is no longer available. Using{' '}
          <strong>{currentLabel}</strong> until you pick another.
        </div>
      )}

      {!display.comparisonAvailable && active.length >= 2 && (
        <div style={styles.stale} role="status">
          Automatic price comparison is unavailable for this item. Countertop is
          using the preferred vendor until the package conversion is reviewed.
        </div>
      )}

      {display.subCentNote && (
        <div style={{ ...styles.note, marginTop: 0, marginBottom: 6 }}>
          Prices differ by {display.subCentNote}.
        </div>
      )}

      {active.map(renderChoice)}

      {resolution.isExplicit && (
        <button
          type="button"
          style={{ ...styles.choice(false), minHeight: 40 }}
          disabled={pendingId !== undefined}
          data-testid="vendor-clear"
          onClick={(event) => { event.stopPropagation(); choose(null); }}
        >
          <span style={{ ...styles.vendor, color: C.body }}>Use automatic lowest-price vendor</span>
          <span style={{ ...styles.meta, display: 'block' }}>
            Removes this line&apos;s override and uses the lowest current comparable price.
          </span>
        </button>
      )}

      <div style={styles.note}>{effectiveNote}</div>
    </div>
  );

  return (
    <span
      ref={wrapRef}
      style={styles.wrap}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={hoverOpen}
      onMouseLeave={hoverClose}
    >
      <button
        ref={triggerRef}
        type="button"
        style={styles.link(compact)}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The vendor names go in the tooltip too, so the list is readable
        // without waiting for the panel and survives a touch device.
        title={
          vendorLabels.length >= 2
            ? `${vendorLabels.join(' · ')} — choose a vendor`
            : `${currentLabel} — choose a different vendor`
        }
        data-testid="vendor-select-trigger"
        onFocus={hoverOpen}
        onClick={(event) => { event.stopPropagation(); cancelHover(); setOpen((v) => !v); }}
      >
        {currentLabel}
        <span aria-hidden="true" style={styles.caret}>▾</span>
      </button>

      {open && !mobile && <div style={styles.popover(align)} role="dialog" aria-label="Choose vendor">{panel}</div>}
      {open && mobile && createPortal(
        <>
          <div data-testid="vendor-select-overlay" style={styles.overlay} onClick={close} />
          <div style={styles.sheet} role="dialog" aria-label="Choose vendor">
            <div style={styles.sheetHandle} />
            {panel}
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}
